import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { PriceFilterRange, RangeFilter } from '~shared/models';
import { Attribute, Tag } from '~app/modules/filters/models';
import { select, Store } from '@ngrx/store';
import * as fromFilters from '~app/modules/filters/store';
import { FiltersApplicationEventService } from './filters-application-event.service';
import {
  AttributeFilterHandler,
  CategoriesFilterHandler,
  PriceFilterHandler,
  PromotionFilterHandler,
  RangeFilterHandler,
} from '~app/modules/filters/classes';
import { FilterRouteService } from './filter-route.service';
import { GeneralUtil } from '~shared/utils/general.util';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { Category } from '~app/modules/generic/models';
import { Params } from '@angular/router';
import { tap } from 'rxjs/operators';
import { NUMBER_1 } from '~shared/constants';
import { FilterHandlerParams } from '~app/modules/filters/models/filter-handler-params';

@Injectable({
  providedIn: 'root',
})
export class FilterTagService {
  private readonly priceFilterTag$: Observable<PriceFilterRange>;
  private readonly categoriesTags$: Observable<Tag<Category>[]>;
  private readonly blackDealTag$: Observable<boolean>;
  private readonly promotionTag$: Observable<boolean>;
  private readonly volumePriceTag$: Observable<boolean>;
  private readonly rebuyGuaranteeTag$: Observable<string>;
  private readonly attributesTags$: Observable<Tag<Attribute>[]>;
  private readonly widthFilterTag$: Observable<RangeFilter>;
  private readonly heightFilterTag$: Observable<RangeFilter>;
  private readonly lengthFilterTag$: Observable<RangeFilter>;
  private readonly diameterFilterTag$: Observable<RangeFilter>;

  private allCategoriesTags: Tag<Category>[];
  private allAttributesTags: Tag<Attribute>[];

  constructor(
    private store: Store<fromFilters.State>,
    private filtersApplicationEventService: FiltersApplicationEventService,
    private filterRouteService: FilterRouteService,
    private productsListUrlService: ProductsListUrlService
  ) {
    this.priceFilterTag$ = this.store.pipe(select(fromFilters.getPriceRangeTag));
    this.widthFilterTag$ = this.store.pipe(select(fromFilters.getWidthTag));
    this.heightFilterTag$ = this.store.pipe(select(fromFilters.getHeightTag));
    this.lengthFilterTag$ = this.store.pipe(select(fromFilters.getLengthTag));
    this.diameterFilterTag$ = this.store.pipe(select(fromFilters.getDiameterTag));
    this.blackDealTag$ = this.store.pipe(select(fromFilters.getBlackDealFilter));
    this.promotionTag$ = this.store.pipe(select(fromFilters.getPromotionFilter));
    this.volumePriceTag$ = this.store.pipe(select(fromFilters.getVolumePriceFilter));
    this.rebuyGuaranteeTag$ = this.store.pipe(select(fromFilters.getRebuyGuaranteeFilter));
    this.categoriesTags$ = this.store.pipe(
      select(fromFilters.getCategoryTags),
      tap((tags: Tag<Category>[]) => {
        this.allCategoriesTags = tags;
      })
    );
    this.attributesTags$ = this.store.pipe(
      select(fromFilters.getAttributeTags),
      tap((tags: Tag<Attribute>[]) => {
        this.allAttributesTags = tags;
      })
    );
  }

  /* Getters */

  public get priceFilterTags(): Observable<PriceFilterRange> {
    return this.priceFilterTag$;
  }

  public get categoriesTags(): Observable<Tag<Category>[]> {
    return this.categoriesTags$;
  }

  public get blackDealTag(): Observable<boolean> {
    return this.blackDealTag$;
  }
  public get promotionTag(): Observable<boolean> {
    return this.promotionTag$;
  }

  public get volumePriceTag(): Observable<boolean> {
    return this.volumePriceTag$;
  }

  public get rebuyGuaranteeTag(): Observable<string> {
    return this.rebuyGuaranteeTag$;
  }

  public get attributesTags(): Observable<Tag<Attribute>[]> {
    return this.attributesTags$;
  }

  public get widthTags(): Observable<RangeFilter> {
    return this.widthFilterTag$;
  }
  public get heightTags(): Observable<RangeFilter> {
    return this.heightFilterTag$;
  }
  public get lengthTags(): Observable<RangeFilter> {
    return this.lengthFilterTag$;
  }

  public get diameterTags(): Observable<RangeFilter> {
    return this.diameterFilterTag$;
  }

  /* public methods */

  removePriceTag(): void {
    this.filtersApplicationEventService.priceTagRemoved();
    this.filterRouteService.applyFilters(PriceFilterHandler.instance.remove());
  }

  removeCategoryTag(category: string, id: string): void {
    this.filtersApplicationEventService.categoryTagRemoved();
    this.filterRouteService.applyFilters(CategoriesFilterHandler.instance.remove(category, id));
  }

  canRemoveCategoryTag(categorySlug: string, params: Params): boolean {
    // If the category is part of localFilters we can remove it
    const categoryTagToRemove = this.allCategoriesTags.find((tag) => tag.value.slug === categorySlug);
    if (categoryTagToRemove.fromLocal) {
      return true;
    }

    // If we have many brands from serverFilters we can remove it
    const responseCategories = this.allCategoriesTags.filter((tag) => tag.fromLocal === false);
    if (NUMBER_1 < responseCategories.length) {
      return true;
    }

    // In this case the category is the only category tag shown and is from serverFilters
    // We would return TRUE if the there is only one category on URL as well
    const paramCategories = params?.filter?.categories || [];
    return paramCategories.length === NUMBER_1;
  }

  removePromotionTag(volumePrices: boolean): void {
    this.filtersApplicationEventService.promotionTagRemoved();
    this.filterRouteService.applyFilters(
      PromotionFilterHandler.instance.remove({
        promotion: {},
        volumePrices,
      })
    );
  }

  removeVolumePriceTag(promotion: boolean): void {
    this.filtersApplicationEventService.volumePriceTagRemoved();
    this.filterRouteService.applyFilters(
      PromotionFilterHandler.instance.remove({
        volumePrices: {},
        promotion,
      })
    );
  }

  removeBlackDealTag(blackDealTag) {
    this.filterRouteService.applyFilters(
      PromotionFilterHandler.instance.remove({
        blackFriday: {},
      })
    );
  }
  removeAttributeTag(attribute: Attribute): void {
    const filterHandlerParams: FilterHandlerParams = AttributeFilterHandler.instance.remove(
      attribute.options[0],
      attribute?.id
    );

    this.filtersApplicationEventService.attributeFilterRemoved(attribute);
    this.filterRouteService.applyFilters(filterHandlerParams);
  }

  canRemoveAttributeTag(attribute: Attribute, params: Params): boolean {
    const selectedAttributeTags: Tag<Attribute>[] = this.allAttributesTags.filter(
      (tag) => tag.value.id === attribute.id
    );

    // If the attribute is part of localFilters we can remove it
    const attributeTagToRemove = selectedAttributeTags.find(
      (tag) => tag.value.options[0].id === attribute.options[0].id
    );
    if (attributeTagToRemove.fromLocal) {
      return true;
    }

    // If we have many attribute options from serverFilters we can remove it
    const responseAttributes = selectedAttributeTags.filter((tag) => !tag.fromLocal);
    if (NUMBER_1 < responseAttributes.length) {
      return true;
    }

    // In this case the attribute option is the only attribute option tag shown and is from serverFilters
    // We would return TRUE if the there is only one attribute option on URL as well
    const paramAttribute = params && params.filter && params.filter.options ? params.filter.options[attribute.id] : [];
    return paramAttribute?.length === NUMBER_1;
  }

  removeRangeTag(type): void {
    this.filterRouteService.applyFilters(RangeFilterHandler.instance.remove(type));
  }

  clearTags(params: Params): void {
    this.store.dispatch(new fromFilters.ClearLocalFilters());
    this.filtersApplicationEventService.clearAllTags();

    if (this.productsListUrlService.isCustomPLPPage()) {
      this.clearCustomPlpTags(params);
      return;
    }
    if (this.productsListUrlService.isBrandPLPPage()) {
      this.clearBrandPlpTags(params);
      return;
    }

    this.filterRouteService.applyFilters({ params: {}, config: { resetPaging: true, resetFilters: true } });
  }

  private clearCustomPlpTags(params: Params): void {
    // When we clear all filters, we should keep promoTag for custom PLP
    // Since it's the filter which defined/distinguish custom PLPs
    const { promoTag, ...rest } = params?.filter;
    // Reset all other filters
    const restCopy = rest ? GeneralUtil.resetObjectValuesToDefault(rest) : {};
    this.filterRouteService.applyFilters({
      params: { filter: { promoTag, ...restCopy } },
      config: { resetPaging: true },
    });
  }

  private clearBrandPlpTags(params: Params): void {
    // In brand PLPs we want to keep categories when clearing all filters
    const { categories, ...rest } = params?.filter;
    const restCopy = rest ? GeneralUtil.resetObjectValuesToDefault(rest) : {};
    this.filterRouteService.applyFilters({
      params: { filter: { categories, ...restCopy } },
      config: { resetPaging: true },
    });
  }
}
